import React, { useState } from 'react';
import { Button } from 'antd';
import styled from 'styled-components';

import { StyledRow } from '../../components/StyledRow';
import { HistoryType, NoteType, RecruitmentHistoryType } from '../recruitments/Recruitments.types';

import { HistoryEntry } from './HistoryEntry';
import { NoteEntry } from './NoteEntry';
import { ReminderEntry } from './ReminderEntry';
import { isHistory, isNote, isReminder } from './typeGuards';
import { ReminderType, useGetRecruitmentReminders } from './useGetRecruitmentReminders';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ScrolledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: scroll;
  overflow-x: hidden;
  height: 700px;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: #f5f5f5;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
  }
`;

const FilterButtonsContainer = styled(StyledRow)`
  justify-content: flex-start;
  margin-bottom: 10px;
  margin-top: 10px;
  gap: 5px;
`;

const ToggleFilterButton = styled(Button)`
  border-radius: 15px;
`;

type RecruitmentHistoryProps = {
  recruitment: RecruitmentHistoryType;
};

export const RecruitmentHistory: React.FC<RecruitmentHistoryProps> = ({ recruitment }) => {
  const { data: remindersData, isLoading: remindersLoading } = useGetRecruitmentReminders(recruitment.uuid || '');
  const [showResponsibles, setShowResponsibles] = useState(false);
  const [showStatuses, setShowStatuses] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  if (!remindersData || remindersLoading) return <></>;

  const elements: (HistoryType | NoteType | ReminderType)[] = [
    ...recruitment.history,
    ...recruitment.notes,
    ...remindersData,
  ];

  const historyItems = elements.sort((a, b) => {
    const firstDate = isNote(a) ? a.createdDate : a.date;
    const secodnDate = isNote(b) ? b.createdDate : b.date;
    return secodnDate - firstDate;
  });

  const FilterButtons: React.FC = () => {
    const toggleFilterResponsibles = () => {
      setShowResponsibles(!showResponsibles);
    };
    const toggleFilterStatuses = () => {
      setShowStatuses(!showStatuses);
    };
    const toggleFilterNotes = () => {
      setShowNotes(!showNotes);
    };

    return (
      <FilterButtonsContainer>
        <ToggleFilterButton onClick={toggleFilterResponsibles} type={showResponsibles ? 'primary' : 'default'}>
          Responsibles
        </ToggleFilterButton>
        <ToggleFilterButton onClick={toggleFilterStatuses} type={showStatuses ? 'primary' : 'default'}>
          Statuses
        </ToggleFilterButton>
        <ToggleFilterButton onClick={toggleFilterNotes} type={showNotes ? 'primary' : 'default'}>
          Notes
        </ToggleFilterButton>
      </FilterButtonsContainer>
    );
  };

  const HistoryItems: React.FC = () => {
    const showAll =
      (showResponsibles && showStatuses && showNotes) || (!showResponsibles && !showStatuses && !showNotes);

    return (
      <ScrolledContainer>
        {historyItems.map((historyItem, index) => {
          if (isNote(historyItem) && (showNotes || showAll)) {
            return <NoteEntry key={`${historyItem.id}`} note={historyItem} />;
          }

          if (isReminder(historyItem)) {
            return <ReminderEntry key={historyItem.uuid} reminder={historyItem} />;
          }

          if (
            isHistory(historyItem) &&
            (showAll ||
              (historyItem.type === 'STATUS' && showStatuses) ||
              (historyItem.type === 'RESPONSIBLE' && showResponsibles) ||
              (historyItem.type === 'PERFECT_MATCH')
            )
          ) {
            return (
              <HistoryEntry key={`${index}_${historyItem.date}_${historyItem.description}`} history={historyItem} />
            );
          }
        })}
      </ScrolledContainer>
    );
  };

  return (
    <MainContainer>
      <FilterButtons />
      <HistoryItems />
    </MainContainer>
  );
};
